import React, { useState, useEffect, useMemo } from "react";
import BaseLayout from "layouts/base-layout";
import BlogItem from "components/blog-item";
import BlogCategoriesNav from "components/blog-categories-nav";
import RichText from "components/rich-text";
import Image from "components/image";
import SearchInput from "components/search-blogs";
import searchForBlogs from "helpers/search-for-blogs";
import Pagination from "components/pagination";
import getUrlParams from "helpers/get-url-params";
import useToggle from "hooks/useToggle";
import { useGlobalContext } from "context/global";

export { Head } from "components/head";

const pageSize = 8;

const BlogCategory = ({ pageContext, location }) => {
	const {
		language, blogs, categories = [], heroBanner = {},
		seoContent, breadcrumbs, meta, pageSettings, header, footer
	} = pageContext;
	const [inputText, setInputText] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [isSearchOpen, toggleSearch] = useToggle();

	useEffect(() => {
		const page = +getUrlParams("page");
		page && setCurrentPage(page <= Math.ceil(blogs.length / pageSize) ? page : 1);
	}, []);

	const blogResults = useMemo(() => {
		if (inputText) return searchForBlogs(blogs, inputText, language);

		const firstPageIndex = (currentPage - 1) * pageSize;
		const lastPageIndex = firstPageIndex + pageSize;
		return blogs.slice(firstPageIndex, lastPageIndex);
	}, [currentPage, inputText]);

	const afterPageChange = () => document.getElementById("blog-categories-nav").scrollIntoView({ behavior: "smooth", block: "start" });

	const Blogs = () => {
		const { translation } = useGlobalContext();
		return (
			blogResults.length > 0 ? (
				<div className="flex flex-wrap justify-center w-full md:gap-10 lg:gap-14 xxl:gap-18 mt-16">
					{blogResults?.map((blog) => (
						<article key={blog.handle} className="md:w-2/5 w-full my-5">
							<BlogItem blog={blog} />
						</article>
					))}
				</div>
			) : (
				<div className="flex justify-center mt-10">
					<h3>{translation.blog?.no_blogs_found}</h3>
				</div>
			)
		);
	};

	return (
		<BaseLayout
			title={meta?.title}
			path={location.pathname}
			header={header}
			footer={footer}
			breadcrumbs={breadcrumbs}
			pageSettings={pageSettings}
		>
			{heroBanner && (
				<div className="relative w-full mb-5 md:mb-14">
					<div className="md:absolute md:left-0 md:top-0 w-full h-full overflow-hidden">
						<div className="blog-hero-banner-content">
							<RichText value={heroBanner?.content} />
						</div>
					</div>
					<Image className="w-full -z-10" image={heroBanner?.image} alt={heroBanner?.image?.alt} />
				</div>
			)}
			<div id="blog-categories-nav" className="flex lg:flex-row-reverse w-full justify-center items-center lg:gap-10 px-5 lg:px-12">
				<SearchInput input={inputText} setInput={setInputText} isOpen={isSearchOpen} toggle={toggleSearch} />
				<BlogCategoriesNav categories={categories} isSearchOpen={isSearchOpen} />
			</div>
			<div className="mx-auto w-full text-center my-5">
				<RichText value={seoContent} />
			</div>
			<Blogs />
			{!inputText && (
				<Pagination
					currentPage={currentPage}
					totalCount={blogs.length}
					pageSize={pageSize}
					onPageChange={(page) => setCurrentPage(page)}
					afterPageChange={afterPageChange}
				/>
			)}
		</BaseLayout>
	);
};

export default BlogCategory;
